import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Badge, Box, Spinner, Stack, Text } from "@chakra-ui/react";

const rowProps = {
    p: 2.5,
    borderRadius: "6px",
    border: "1px solid rgba(244, 67, 54, 0.18)",
    background: "rgba(244, 67, 54, 0.05)",
};

const declineChipProps = {
    background: "rgba(244, 67, 54, 0.18)",
    color: "rgba(229, 115, 115, 1)",
    border: "1px solid rgba(244, 67, 54, 0.4)",
    fontWeight: 600,
    fontSize: "0.65rem",
    fontFamily: "monospace",
};

/**
 * Normalizes the payment failures payload into a plain array.
 * Accepts raw arrays, the admin { items, count } envelope, and
 * { data } / { failures } envelopes.
 * @param {*} data - Decrypted API payload
 * @returns {Array}
 */
function normalizeFailures(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.failures)) return data.failures;
    return [];
}

/**
 * Stripe charges store `created` as a unix timestamp; fall back to ISO strings.
 * @param {Object} failure - Failure object
 * @returns {string}
 */
function formatFailureDate(failure) {
    if (failure?.created && typeof failure.created === "number") {
        return dayjs.unix(failure.created).format("DD/MM/YYYY HH:mm");
    }
    const iso = failure?.created_at || failure?.timestamp || failure?.date;
    if (!iso) return "—";
    const parsed = dayjs(iso);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Stripe amounts are in cents. Returns formatted "X.XX CUR" or "—".
 * @param {Object} failure - Failure object
 * @returns {string}
 */
function formatFailureAmount(failure) {
    const amount = failure?.amount;
    if (amount === null || amount === undefined) return "—";
    return `${(amount / 100).toFixed(2)} ${(failure?.currency || "").toUpperCase()}`.trim();
}

/**
 * Fetches the payment failures for a given subscription.
 * @param {string|number} subscriptionId - Target subscription ID
 * @returns {{ items: Array, loading: boolean, error: string|null }}
 */
function usePaymentFailures(subscriptionId) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchFailures = useCallback(async (id, signal) => {
        setLoading(true); setError(null);
        try {
            const { data } = await axios.get(`/admin/subscriptions/${id}/payment-failures`);
            if (!signal.aborted) setItems(normalizeFailures(data));
        } catch (err) {
            if (!signal.aborted) setError(err?.response?.data || "Impossible de charger les échecs");
        } finally {
            if (!signal.aborted) setLoading(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        if (subscriptionId) fetchFailures(subscriptionId, controller.signal);
        else setItems([]);
        return () => controller.abort();
    }, [subscriptionId, fetchFailures]);

    return { items, loading, error };
}

/**
 * Renders a single payment failure row with date, amount, decline code and message.
 * @param {Object} props
 * @param {Object} props.failure - Failure object
 * @returns {React.ReactElement}
 */
function FailureRow({ failure }) {
    return (
        <Box {...rowProps}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Text color="rgba(255, 255, 255, 0.85)" fontSize="0.78rem" fontWeight={600}>
                    {formatFailureAmount(failure)}
                </Text>
                <Text color="rgba(255, 255, 255, 0.55)" fontSize="0.7rem">
                    {formatFailureDate(failure)}
                </Text>
            </Stack>
            {failure?.decline_code && (
                <Box mb={1}>
                    <Badge {...declineChipProps} px={2} borderRadius="full" textTransform="none">
                        {failure.decline_code}
                    </Badge>
                </Box>
            )}
            {failure?.error_message && (
                <Text color="rgba(255, 255, 255, 0.7)" fontSize="0.75rem">
                    {failure.error_message}
                </Text>
            )}
        </Box>
    );
}

/**
 * Payment failures sub-tab. Lists failed Stripe attempts with code + message.
 * @param {Object} props
 * @param {string|number} props.subscriptionId - Target subscription ID
 * @returns {React.ReactElement}
 */
function PaymentFailuresList({ subscriptionId }) {
    const { items, loading, error } = usePaymentFailures(subscriptionId);

    if (loading) {
        return <Box display="flex" justifyContent="center" py={3}><Spinner size="md" color="#17dcb8" /></Box>;
    }
    if (error) return <Text color="rgba(229, 115, 115, 1)" fontSize="0.85rem">{typeof error === "string" ? error : "Erreur"}</Text>;
    if (items.length === 0) return <Text color="rgba(255, 255, 255, 0.5)" fontSize="0.85rem">Aucun échec de paiement</Text>;

    return <Stack spacing={2}>{items.map((f, i) => <FailureRow key={f?.id || i} failure={f} />)}</Stack>;
}

export default PaymentFailuresList;
